import { useCallback, useEffect, useState } from 'react';
import { MenuTypeCode, SettingsCodes } from '@/enums/enums';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useGetMenus } from '@/services/queries/useMenuQuery';
import useLanguageHelper from '@/hooks/useLanguageHelper';

import { FloatingSwitch } from '@/components/FloatingSwitch/FloatingSwitch';
import { useScrollContext } from '@/context/ScrollContext';
import { type Menu } from '@/models/Menu';
import { useRouter } from '@/routes/hooks/useRouter';
import { HomeIcon, SearchIcon } from '@/assets/icons';
import routesConfig, { type AppRoute } from '@/routes/routes';
import MobileMenu from './MobileMenu';
import { useGetSettingsByCodes } from '@/services/queries/useSettingQuery';
import { Lookups } from '@/constants/constants';

const ministryLogoUrl = '/assets/logos/ministry-logo.svg';
const electionsLogoUrl = '/assets/logos/elections-logo.svg';

const Header = () => {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [menuType, setMenuType] = useState<string>(MenuTypeCode.Parliamentary);
  const { currentLanguageId } = useLanguageHelper('common');
  const { data } = useGetMenus(currentLanguageId, menuType);
  const router = useRouter();

  const { data: currentType } = useGetSettingsByCodes(SettingsCodes.CRNTTYPE);

  // Optimized hook that only causes re-render when threshold is crossed
  const { isScrolled, isInnerPage } = useScrollContext();

  const theme = useTheme();
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const xlLgBetween = useMediaQuery(theme.breakpoints.between('lg', 1450));

  const logoWidth = !xlLgBetween && !lgDown ? (isScrolled ? '6rem' : '10.9375rem') : '6rem';
  const logoHeight = !xlLgBetween && !lgDown ? (isScrolled ? '5rem' : '9.375rem') : '5rem';

  const handleMouseEnter = useCallback((id: number) => {
    setOpenMenuId(id);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setOpenMenuId(null);
  }, []);

  const handleParentClick = useCallback(
    (menu: Menu) => {
      const pageUrl = menu.pageURL ?? '';
      let isValidURL = false;

      try {
        const url = new URL(pageUrl);
        isValidURL = url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        isValidURL = false;
      }

      if (isValidURL) {
        window.open(pageUrl, '_blank');
        return;
      }
      const selectedRoute: AppRoute | undefined = routesConfig.find(
        (route) => route.path === pageUrl,
      );

      if (selectedRoute) {
        router.push(selectedRoute.name);
        return;
      }

      router.push('MENU_DETAILS', { parentId: menu.id, childId: menu.children[0].id ?? 0 });
    },
    [router],
  );

  const handleChildClick = useCallback(
    (menu: Menu) => {
      if (menu.pageURL) {
        const pageUrl = menu.pageURL ?? '';

        let isValidURL = false;

        try {
          const url = new URL(pageUrl);
          isValidURL = url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
          isValidURL = false;
        }
        if (isValidURL) {
          window.open(pageUrl, '_blank');
          return;
        }

        const selectedRoute: AppRoute | undefined = routesConfig.find(
          (route) => route.path === pageUrl,
        );

        if (selectedRoute) {
          router.push(selectedRoute.name);
          return;
        }
      } else {
        router.push('MENU_DETAILS', { parentId: menu.parentId!, childId: menu.id });
        //void router.refresh();
      }
    },
    [router],
  );

  useEffect(() => {
    if (currentType && currentType.length > 0) {
      const currentTypeCode = Lookups.find(
        (lookup) => lookup.id.toString() === currentType[0].value,
      )?.code;
      if (currentTypeCode) setMenuType(currentTypeCode as MenuTypeCode);
    }
  }, [currentType]);

  return (
    <AppBar
      color='default'
      elevation={1}
      position='fixed'
      sx={{
        boxShadow: '0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.30)',
        zIndex: 1100,
        bgcolor: 'white',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          justifyContent: 'space-between',
          height: isScrolled ? '4.375rem' : '8.125rem',
          transition: 'height 0.3s ease-in-out',
          py: 0,
          px: '1.2rem',
        }}
      >
        {/* Logos */}
        <Stack alignItems='center' direction='row' spacing='0.5rem'>
          <Box
            component='img'
            alt='Lebanon Elections'
            src={electionsLogoUrl}
            sx={{
              marginTop: isScrolled ? '0.5rem !important' : '1rem',
              cursor: 'pointer',
              width: logoWidth,
              height: logoHeight,
              transition: 'all 0.3s ease-in-out',
            }}
            onClick={() => {
              router.push('HOME');
            }}
          />
          <Box
            component='img'
            alt='Ministry of Interior'
            src={ministryLogoUrl}
            sx={{
              cursor: 'pointer',
              width: logoWidth,
              height: logoHeight,
              transition: 'all 0.3s ease-in-out',
            }}
            onClick={() => {
              router.push('HOME');
            }}
          />

          <FloatingSwitch initialPosition='menu' selectedType={menuType} onToggle={setMenuType} />
        </Stack>
        {/* Menu Start */}
        <Stack
          direction='row'
          flexGrow={1}
          sx={{ display: lgDown ? 'none' : 'flex', gap: '.6rem' }}
        >
          {/* Floating Switch */}
          {/* Home Icon */}
          <IconButton>
            <HomeIcon onClick={() => router.push('HOME')} />
          </IconButton>

          {/* Menu */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              gap: '0.2rem',
            }}
          >
            {data?.map((menu) => (
              <Box
                key={`${menu.id}-parent-menu`}
                onMouseEnter={() => handleMouseEnter(menu.id)}
                onMouseLeave={handleMouseLeave}
              >
                <Typography
                  sx={{
                    fontWeight: '600',
                    fontSize: xlLgBetween ? (isScrolled ? '0.55rem' : '0.6rem') : '0.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleParentClick(menu)}
                >
                  {menu.menuDetail.title}
                  {menu.children && menu.children.length > 0 && (
                    <KeyboardArrowDownIcon fontSize='small' />
                  )}
                </Typography>

                {menu.children && menu.children.length > 0 && menu.id === openMenuId && (
                  <List
                    sx={{
                      position: 'fixed',
                      background: '#FFF',
                      borderRadius: '1.25rem',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                      minWidth: '12.5rem',
                    }}
                  >
                    {menu.children.map((childMenu) => (
                      <ListItem key={`${childMenu.id}-child-listItem`} disablePadding>
                        <ListItemButton
                          key={`${childMenu.id}-child-listItemButton`}
                          onClick={() => {
                            handleChildClick(childMenu);
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: '600',
                              fontSize: '0.6rem',
                              whiteSpace: 'nowrap',
                              minWidth: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              height: '100%',
                            }}
                          >
                            {childMenu.menuDetail.title}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            ))}
          </Box>

          {/* Search Icon */}
          <IconButton onClick={() => router.push('SEARCH_RESULTS')}>
            <SearchIcon />
          </IconButton>
        </Stack>

        {/* Mobile Menu */}
        <MobileMenu
          data={data}
          handleParentClick={handleParentClick}
          handleChildClick={handleChildClick}
        />
      </Toolbar>
      {!isInnerPage && !lgDown && (
        <FloatingSwitch initialPosition='floating' selectedType={menuType} onToggle={setMenuType} />
      )}
    </AppBar>
  );
};

export default Header;
